// Press Releases index page — lists all press releases newest-first.
// New releases automatically appear here as they are added to
// `/app/frontend/src/data/pressReleases.js`.

import { Link } from 'react-router-dom';
import { Newspaper, ArrowRight, Calendar, MapPin } from 'lucide-react';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { JoyUnderline } from '../components/JoyAccents';
import { useI18n } from '../contexts/I18nContext';
import { pressReleases } from '../data/pressReleases';

const ACCENT_MAP = {
  coral: { rail: '#E5614C', chip: 'rgba(229,97,76,0.10)', text: '#E5614C' },
  teal: { rail: '#5DCAB5', chip: 'rgba(93,202,181,0.12)', text: '#5DCAB5' },
  yellow: { rail: '#FCD968', chip: 'rgba(252,217,104,0.18)', text: '#B8912A' },
};

export default function PressReleasesPage() {
  const { t } = useI18n();
  const sorted = [...pressReleases].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <PageShell
      eyebrow={<><Newspaper className="h-4 w-4" /> {t('press.eyebrow')}</>}
      title={<>{t('press.title1')} <span className="italic text-[#E5614C]">{t('press.title2')}</span></>}
      subtitle={t('press.subtitle')}
      accent="coral"
    >
      <SEO
        title="Press Releases"
        description="News, announcements, and press releases from GEMS @ The Arts — a community arts and enrichment center in Framingham, MA."
        path="/press-releases"
      />

      <section className="pb-24" data-testid="press-list">
        <div className="max-w-5xl mx-auto px-5 md:px-10 space-y-6 lg:space-y-8">
          {sorted.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#2A1B1A]/15 bg-white p-12 text-center">
              <p className="text-[15px] text-[#2A1B1A]/60">{t('press.empty')}</p>
            </div>
          ) : (
            sorted.map((r) => {
              const cl = ACCENT_MAP[r.accent] || ACCENT_MAP.coral;
              return (
                <Link
                  key={r.slug}
                  to={`/press-releases/${r.slug}`}
                  data-testid={`press-card-${r.slug}`}
                  className="group block relative bg-white rounded-[24px] border border-[#2A1B1A]/8 overflow-hidden hover:shadow-[0_28px_60px_-25px_rgba(42,27,26,0.3)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: cl.rail }} aria-hidden="true" />
                  <div className="p-7 sm:p-10 lg:p-12">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: cl.text }}>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: cl.chip }}>
                        <Calendar className="h-3.5 w-3.5" /> {r.dateLabel}
                      </span>
                      {r.location && (
                        <span className="inline-flex items-center gap-1.5 text-[#2A1B1A]/55">
                          <MapPin className="h-3.5 w-3.5" /> {r.location}
                        </span>
                      )}
                    </div>
                    <h2 className="mt-5 font-serif-display text-[26px] sm:text-[32px] lg:text-[38px] font-bold text-[#2A1B1A] leading-[1.08] group-hover:text-[#E5614C] transition-colors">
                      {r.title}
                    </h2>
                    <p className="mt-4 text-[15.5px] text-[#2A1B1A]/70 leading-relaxed">{r.excerpt}</p>
                    <div className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-semibold" style={{ color: cl.text }}>
                      {t('press.readMore')} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </section>

      {/* Media contact strip */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="rounded-[28px] bg-[#FAF3EE] border border-[#2A1B1A]/8 p-10 lg:p-14 text-center">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#5DCAB5]" /> {t('press.contact.eyebrow')}
            </div>
            <h2 className="font-serif-display mt-4 text-[28px] sm:text-[36px] font-bold text-[#2A1B1A] leading-[1.05]">
              {t('press.contact.title1')} <JoyUnderline color="#E5614C">{t('press.contact.title2')}</JoyUnderline>?
            </h2>
            <p className="mt-4 text-[15px] text-[#2A1B1A]/70 max-w-xl mx-auto">
              {t('press.contact.body')}
            </p>
            <p className="mt-5 text-[14px]">
              <a href="mailto:info@gemsatthearts.org" className="text-[#E5614C] font-semibold hover:underline">
                info@gemsatthearts.org
              </a>
              <span className="mx-2 text-[#2A1B1A]/30">·</span>
              <a href="tel:5084052787" className="text-[#2A1B1A]/70 hover:text-[#E5614C]">508-405-2787</a>
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
